import { useNavigate } from 'react-router'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  LayoutDashboard,
  Users,
  FileText,
  Mail,
  Receipt,
  Shield,
  Activity,
  GitBranch,
  Settings,
  RefreshCw,
  Bell,
  BarChart3,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react'
import { useAuth } from '@/providers/auth-provider'
import { useTheme } from '@/providers/theme-provider'

const adminNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/admin' },
  { icon: Users, label: 'Clients', href: '/admin/clients' },
  { icon: FileText, label: 'Blog', href: '/admin/blog' },
  { icon: Mail, label: 'Newsletters', href: '/admin/newsletters' },
  { icon: Receipt, label: 'Invoices', href: '/admin/invoices' },
  { icon: Shield, label: 'Security', href: '/admin/security' },
  { icon: Activity, label: 'Activity Log', href: '/admin/activity' },
  { icon: GitBranch, label: 'GitHub Sync', href: '/admin/github' },
  { icon: Settings, label: 'Settings', href: '/admin/settings' },
]

const portalNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/portal' },
  { icon: FileText, label: 'Blog', href: '/portal/blog' },
  { icon: Mail, label: 'Newsletter', href: '/portal/newsletter' },
  { icon: Receipt, label: 'Invoices', href: '/portal/invoices' },
  { icon: RefreshCw, label: 'Changes', href: '/portal/changes' },
  { icon: Bell, label: 'Alerts', href: '/portal/alerts' },
  { icon: Users, label: 'Community', href: '/portal/community' },
  { icon: BarChart3, label: 'Insights', href: '/portal/insights' },
  { icon: Settings, label: 'Settings', href: '/portal/settings' },
]

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  role: 'admin' | 'client'
}

/**
 * CommandPalette — ⌘K overlay with navigation and quick actions.
 *
 * Uses shadcn/ui Command (built on cmdk).
 * Falls back to hardcoded nav items — no API dependency.
 * Selecting a nav item navigates and closes the palette.
 */
export function CommandPalette({ open, onOpenChange, role }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()

  const navItems = role === 'admin' ? adminNavItems : portalNavItems

  function select(href: string) {
    navigate(href)
    onOpenChange(false)
  }

  function toggleTheme() {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
    onOpenChange(false)
  }

  function handleLogout() {
    onOpenChange(false)
    logout()
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages and actions…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigation">
          {navItems.map(({ icon: Icon, label, href }) => (
            <CommandItem key={href} onSelect={() => select(href)}>
              <Icon className="mr-2 h-4 w-4" />
              {label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <CommandItem onSelect={toggleTheme}>
            {resolvedTheme === 'dark'
              ? <Sun className="mr-2 h-4 w-4" />
              : <Moon className="mr-2 h-4 w-4" />}
            {resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          </CommandItem>
          <CommandItem onSelect={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
